import { axiosSwagger } from "@/axios";

export const namespaced = true;

/* ---------------------------------- */
/* Helpers */
/* ---------------------------------- */

function getUserDeliveryUnitId() {
  const userData =
    localStorage.getItem("USER") &&
    JSON.parse(localStorage.getItem("USER"));

  return userData?.deliveryUnit?.id;
}

/* ---------------------------------- */
/* State */
/* ---------------------------------- */

export const state = {
  loading: false,
  uploadProgress: 0,
  backendProgress: 0,

  pending: [],
  available: [],
  accepted: [],
};

/* ---------------------------------- */
/* Getters */
/* ---------------------------------- */

export const getters = {
  hasPending: (state) => state.pending.length > 0,
  hasAvailable: (state) => state.available.length > 0,
  hasAccepted: (state) => state.accepted.length > 0,
};

/* ---------------------------------- */
/* Mutations */
/* ---------------------------------- */

export const mutations = {
  setLoading(state, val) {
    state.loading = val;
  },

  setUploadProgress(state, val) {
    state.uploadProgress = val;
  },

  setBackendProgress(state, val) {
    state.backendProgress = val;
  },

  setPending(state, data) {
    state.pending = data;
  },

  setAvailable(state, data) {
    state.available = data;
  },

  setAccepted(state, data) {
    state.accepted = data;
  },

  resetProgress(state) {
    state.uploadProgress = 0;
    state.backendProgress = 0;
  },
};

/* ---------------------------------- */
/* Actions */
/* ---------------------------------- */

export const actions = {
  /* ============================= */
  /* Upload File */
  /* ============================= */
  async uploadFile({ commit, dispatch }, { file, randomFileId }) {
    commit("setLoading", true);
    commit("resetProgress");

    const deliveryUnitId = getUserDeliveryUnitId();

    try {
      const formData = new FormData();
      formData.append("file", file);

      await axiosSwagger.post(
        `/api/AiFileManager/Upload/${deliveryUnitId}/${randomFileId}/${file.size}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },

          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              commit("setUploadProgress", percent);
            }
          },
        }
      );

      // After upload, refresh pending list
      await dispatch("fetchPending");
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  /* ============================= */
  /* Fetch Pending */
  /* ============================= */
  async fetchPending({ commit }) {
    const deliveryUnitId = getUserDeliveryUnitId();
    const { data } = await axiosSwagger.get(
      `/api/AiFileManager/Pending/${deliveryUnitId}`
    );
    commit("setPending", data || []);
  },

  /* ============================= */
  /* Fetch Available */
  /* ============================= */
  async fetchAvailable({ commit }) {
    const deliveryUnitId = getUserDeliveryUnitId();
    const { data } = await axiosSwagger.get(
      `/api/AiFileManager/Available/${deliveryUnitId}`
    );
    commit("setAvailable", data || []);
  },

  /* ============================= */
  /* Fetch Accepted */
  /* ============================= */
  async fetchAccepted({ commit }) {
    const deliveryUnitId = getUserDeliveryUnitId();
    const { data } = await axiosSwagger.get(
      `/api/AiFileManager/Accepted/${deliveryUnitId}`
    );
    commit("setAccepted", data || []);
  },

  /* ============================= */
  /* Accept File */
  /* ============================= */
  async acceptFile({ dispatch }, { id, fileFolderId }) {
    await axiosSwagger.post(
      `/api/AiFileManager/Accept/${id}/${fileFolderId}`
    );

    await dispatch("refreshAll");
  },

  /* ============================= */
  /* Update File Details */
  /* ============================= */
  async updateDetails(_, { id, payload }) {
    await axiosSwagger.post(
      `/api/AiFileManager/UpdateDetails/${id}`,
      payload
    );
  },

  /* ============================= */
  /* Refresh Everything */
  /* ============================= */
  async refreshAll({ dispatch }) {
    await Promise.all([
      dispatch("fetchPending"),
      dispatch("fetchAvailable"),
      dispatch("fetchAccepted"),
    ]);
  },

  /* ============================= */
  /* SignalR Hook (call from component) */
  /* ============================= */
  registerSignalREvents({ commit, dispatch }) {
    if (!this._vm.$socket) return;

    // Backend processing progress
    this._vm.$socket.on("ReceiveProgress", (percent) => {
      commit("setBackendProgress", percent);
    });

    // When backend finishes processing
    this._vm.$socket.on("FileProcessed", async () => {
      await dispatch("refreshAll");
    });

    // When file accepted elsewhere
    this._vm.$socket.on("FileAccepted", async () => {
      await dispatch("refreshAll");
    });
  },
};